import { useState } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import { api, bearer } from '../api/client';
import { productIsProvisioned } from '../lib/provisioning';
import { logEvent } from '../lib/telemetry';
import { errorType } from '../lib/telemetry/errorType';

const MESSAGES: Record<string, string> = {
  already_active: "You already have this product.",
  already_trialled: "This organisation has already used its trial for this product.",
  unknown_product: "That product isn't available for trial.",
  forbidden: "You need to be an organisation admin to start a trial.",
};

export type StartTrialOutcome = 'started' | 'already' | 'transient' | 'forbidden' | 'rejected';

// The refresh only freshens the session's entitlements; a hung one must not hold the started
// trial's success (and the setup watch behind it) hostage.
export const REFRESH_TIMEOUT_MS = 5_000;

// A later attempt can succeed after a 5xx, a rate limit or an expired token (the next start
// reads a fresh one); any other 4xx is the API refusing this trial for good.
function isTransient(status: number | undefined): boolean {
  return status !== undefined && (status >= 500 || status === 429 || status === 401);
}

// One hook serves every trialable product on the page (hooks can't be called per-card in a
// map): start() takes the product, and busyProducts tracks each in-flight card independently,
// so starting one trial never silently swallows a click on another card.
export function useStartTrial({ onStarted }: { onStarted?: (productKey: string) => void } = {}) {
  const auth = useAuth() as { getAccessToken?: (opts?: { forceRefresh?: boolean }) => Promise<string | null | undefined> };
  const [busyProducts, setBusyProducts] = useState<ReadonlySet<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const clear = () => { setError(null); setSuccess(null); };

  // The session token still carries the pre-trial entitlements; without a refresh, products
  // show "Access required" until a reload. Best-effort: the trial has already started.
  async function refreshSession() {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<'timeout'>((resolve) => { timer = setTimeout(() => resolve('timeout'), REFRESH_TIMEOUT_MS); });
    try {
      const refresh = Promise.resolve(auth.getAccessToken?.({ forceRefresh: true })).then(() => 'done' as const);
      if (await Promise.race([refresh, timeout]) === 'timeout') {
        logEvent('cloudhouse.enterprise.trial.token_refresh_timed_out', 'Token refresh after trial start timed out',
          { timeout_ms: REFRESH_TIMEOUT_MS }, 'WARN');
      }
    } catch (e) {
      logEvent('cloudhouse.enterprise.trial.token_refresh_failed', 'Token refresh after trial start failed',
        { 'error.type': errorType(e) }, 'WARN');
    } finally {
      clearTimeout(timer);
    }
  }

  async function start(product: string): Promise<StartTrialOutcome> {
    if (busyProducts.has(product)) return 'rejected';
    clear();
    setBusyProducts((prev) => new Set(prev).add(product));
    try {
      const token = await auth.getAccessToken?.();
      if (!token) { setError("We couldn't verify your session. Please refresh and try again."); return 'transient'; }
      const { error: apiError, response } = await api.POST('/trials/{productId}', {
        params: { path: { productId: product } },
        headers: bearer(token),
      });
      // openapi-fetch leaves `error` undefined for a non-2xx with an empty body (a bare 401 or
      // 404 from the edge), so the status decides success, not the absence of an error body.
      if (apiError || !response?.ok) {
        const code = (apiError as { error?: string } | undefined)?.error;
        setError((code && MESSAGES[code]) ?? "We couldn't start the trial. Please try again.");
        if (code === 'already_trialled' || code === 'already_active') return 'already';
        if (code === 'forbidden') return 'forbidden';
        return isTransient(response?.status) ? 'transient' : 'rejected';
      }
      await refreshSession();
      setSuccess('Trial started — your product is now active.');
      // Celebration is best-effort: lazily loaded, and a failure must never affect the flow.
      // A provisioned product's trial only starts a wait, so its celebration fires when the
      // build turns ready (EnterpriseHub) rather than here.
      if (!productIsProvisioned(product)) {
        void import('canvas-confetti')
          .then(({ default: confetti }) =>
            confetti({ particleCount: 140, spread: 75, origin: { y: 0.7 }, disableForReducedMotion: true }))
          .catch(() => {});
      }
      onStarted?.(product);
      return 'started';
    } catch {
      setError("We couldn't start the trial. Please try again.");
      return 'transient';
    } finally {
      setBusyProducts((prev) => {
        const next = new Set(prev);
        next.delete(product);
        return next;
      });
    }
  }

  return { start, busyProducts, error, success, clear };
}
